import { gql, useQuery, useFragment } from "@apollo/client";
import {
  RECOMMENDATION_SECTION_MODULE_FRAGMENT,
  RECOMMENDATION_SECTION_FRAGMENT
} from "lib/apollo/fragments";
import { createRequestContext, getClient } from "lib/apolloClient";
import type {
  RecommendationSectionModule,
  RecommendationSection
} from "@shared/graphql.types";

type RecommendationSource = "constructor";
type Recommender<TRecommenderType extends string> =
  `${RecommendationSource}:${TRecommenderType}`;

interface ComplementaryRecommendationConfig {
  complementaryToSku: string;
  recommender: Recommender<
    "pdp-complimentary" | "atb-confirmation-complimentary"
  >;
}

interface AlternativeRecommendationConfig {
  alternativeToSku: string;
  recommender: Recommender<"pdp-out-of-stock-alternatives">;
}

interface BestsellersRecommendationConfig {
  recommender: Recommender<"basket-page-bestsellers">;
}

export type GetRecommendationType = {
  locationType: string;
  config: RecommendationConfig;
};

export type RecommendationConfig =
  | ComplementaryRecommendationConfig
  | AlternativeRecommendationConfig
  | BestsellersRecommendationConfig;

export const GET_RECOMMENDATIONS = gql`
  query getRecommendations($recommendations: [RecommendationInput!]!) {
    getRecommendations(recommendations: $recommendations) {
      ...RecommendationSectionModuleFragment
    }
  }
  ${RECOMMENDATION_SECTION_MODULE_FRAGMENT}
`;
export const useRecommendations = (recommendations: GetRecommendationType[]) =>
  useQuery<{
    getRecommendations: RecommendationSectionModule;
  }>(GET_RECOMMENDATIONS, {
    variables: { recommendations },
    client: getClient(),
    context: createRequestContext({
      waitForFingerPrint: true,
      usePostForQuery: true,
      sessionStrategy: "START_NEW_ANONYMOUS"
    })
  });

export const useProductRecommendationFragment = (
  recommendationId: string
): RecommendationSection | null => {
  const { data, complete } = useFragment<RecommendationSection>({
    fragment: RECOMMENDATION_SECTION_FRAGMENT,
    from: {
      __typename: "RecommendationSection",
      id: recommendationId
    },
    fragmentName: "RecommendationSectionFragment"
  });

  if (!complete || !data) {
    return null;
  }

  return data;
};
